import React, { useEffect, useRef, useState } from 'react';

import { ArrowRightIcon } from '$partials/icon';
import { Button } from '$partials/ui/index.js';
import { formatLinkBlockToUrl } from '$utilities/format.js';
import { Image } from '$partials/media/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').KeyLinks}
 */
const KeyLinks = ({
    image,
    items,
    label,
    title,
    video,
    namespace,
    caption
}) => {
    // misc
    const isPartnershipPage = namespace?.includes('partnership');

    // references
    const videoRef = useRef();

    // state
    const [videoHeight, setVideoHeight] = useState(undefined);
    const [videoWidth, setVideoWidth] = useState(undefined);

    // methods
    const handleVideoLoad = () => {
        setVideoHeight(videoRef.current.videoHeight);
        setVideoWidth(videoRef.current.videoWidth);
    };

    const isLandscape = () => {
        if (video) {
            return videoWidth > videoHeight;
        }
        return image.width > image.height;

    };

    // hooks
    useEffect(() => {
        const video = videoRef.current;

        if (video) {
            video.addEventListener('loadedmetadata', handleVideoLoad, { passive: true });

            window.setTimeout(() => {
                handleVideoLoad();
            }, 100);
        }

        return () => {
            if (video) {
                video.removeEventListener('loadedmetadata', handleVideoLoad, { passive: true });
            }
        };
    }, []);

    // render
    return (
        <>
            <div className='absolute trunk-y-bleed translate-yn-100 zn1'>
                <div className='bg-peach height-7' />
            </div>

            <div className='bg-peach trunk-full-width black'>
                <div className={`${isPartnershipPage ? '' : 'flex-col-reverse'} container col-12 flex flex-wrap sm-flex-no-wrap sm-flex-row justify-between pt6 pb9 md-py9`}>
                    <div className={`${isPartnershipPage ? 'pb8' : ''} col-12 sm-col-6`}>
                        <div className='col-12 md-col-10'>
                            {label && <p className='type-style-5 mb3'>{label}</p>}
                            <h2 className='type-style-1 sans-serif black'>{title}</h2>
                        </div>

                        <div className='col-12 md-col-8 sm-pr7 mt5 md-pr6'>
                            {items.map((item, i) => {
                                const link = formatLinkBlockToUrl(item.link);

                                return (
                                    <Button
                                        as='a'
                                        className = 'bg-none type-style-5 link-reset'
                                        href={link}
                                        key={i}
                                        look='underline'
                                        renderIcon={<ArrowRightIcon/>}
                                        target={item.link?.new_window || item.link?.linkTo === 'file' ? '_blank' : ''}>
                                        {item.label}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>

                    {(image || video) && (
                        <div className={`col-12 mb6 sm-mb0 sm-pl9 md-pl0 md-pr12${isLandscape() ? ' sm-col-7 md-col-6' : ' sm-col-6 md-col-5'}`}>
                            <div style={{maxWidth: isLandscape() ? '100%' : '500px'}}>
                                {video ? (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        ref={videoRef}
                                        src={video.videoUrl}/>
                                ) : (
                                    <Image {...image}/>
                                )}
                            </div>
                            {caption && (
                                <p className='type-style-5 pt5'>
                                    {caption}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className='absolute trunk-y-bleed zn1'>
                <div className='bg-peach height-7' />
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default KeyLinks;
